import React, { createContext, useState, useContext, useEffect } from 'react';
import api, { tokenStorage } from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = tokenStorage.get(); // Uses sessionStorage (with localStorage migration)
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/api/auth/profile');
      setUser(response.data);
    } catch (error) {
      // Only clear token if server explicitly returned 401 (invalid/expired token)
      // Do NOT clear token on transient network blips or mobile offline events
      if (error.response && error.response.status === 401) {
        tokenStorage.remove();
        delete api.defaults.headers.common['Authorization'];
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, options = {}) => {
    const payload = { email, password };
    if (options.client_location) {
      payload.client_location = options.client_location;
    }
    if (options.hod_pin) {
      payload.hod_pin = options.hod_pin;
    }

    const response = await api.post('/api/auth/login', payload);

    // If backend signals that an HOD PIN is required, return the signal
    // without setting a token (no token was issued yet).
    if (response.data?.requires_hod_pin) {
      return response.data; // { requires_hod_pin: true, message: '...' }
    }

    const { token, user: userData } = response.data;
    tokenStorage.set(token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    return userData;
  };

  const securityLogin = async (email, password, options = {}) => {
    const payload = { email, password };
    if (options.client_location) {
      payload.client_location = options.client_location;
    }

    const response = await api.post('/api/auth/security-login', payload);
    const { token, user: userData } = response.data;
    tokenStorage.set(token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    return userData;
  };

  const logout = () => {
    tokenStorage.remove();
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, securityLogin, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}
